import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Link,
  Image,
} from "@react-pdf/renderer";
import moment from "moment";
import logo from "../../assets/Main_Logo_AI_Colored_Padding.png";

// ✅ Styles
const styles = StyleSheet.create({
  page: {
    padding: 20,
    paddingBottom: 60, // reserve room for footer
    fontSize: 12,
    fontFamily: "Helvetica",
  },
  header: { fontSize: 18, marginBottom: 10, fontWeight: "bold", textAlign: "center", color: "rgb(0, 145, 70)" },
  section: { marginBottom: 10, paddingBottom: 5, borderBottom: "1px solid #ddd" },
  label: { fontWeight: "bold", fontSize: 14, marginBottom: 4 },
  text: { fontSize: 12, marginBottom: 5 },
  chipContainer: { marginTop: 5, marginBottom: 10 },
  chip: { backgroundColor: "green", color: "white", padding: "3px 10px", borderRadius: 5, fontSize: 12, display: "inline-block" },
  table: { display: "table", width: "100%", marginTop: 10, border: "1px solid #ddd" },
  tableRow: { flexDirection: "row", borderBottom: "1px solid #ddd" },
  tableHeader: { fontWeight: "bold", backgroundColor: "#f8f9fa", padding: 5 },
  tableCell: { flex: 1, padding: 5, fontSize: 11 },

  // ✅ Footer
  footer: {
    position: "absolute",
    bottom: 16,
    left: 20,
    right: 20,
    height: 24,
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
    paddingTop: 6,
    fontSize: 9,
    color: "#6b7280",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  footerLeft: { fontSize: 9 },
  footerBrand: { flexDirection: "row", alignItems: "center" },
  logo: { width: 60, marginRight: 4 },
});

// ✅ Footer (fixed on every page)
const Footer = () => {
  const dateText = moment().format("M/D/YYYY");
  return (
    <View style={styles.footer} fixed>
      <Text style={styles.footerLeft}>
        {`Generated by Portend AI • Confidential • ${dateText}`}
      </Text>
      <View style={styles.footerBrand}>
        {logo ? <Image style={styles.logo} src={logo} /> : null}
      </View>
    </View>
  );
};

// ✅ PDF Component
const AttackSurfacePDF = ({ data, pageTitle }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Title */}
      <Text style={styles.header}>{pageTitle}</Text>

      {/* Target & Report Date */}
      <View style={styles.section}>
        <Text style={styles.label}>
          Target: <Text>{data.target || data?.result?.domainInfo?.domain}</Text>
        </Text>
        <Text style={styles.text}>
          Report Date: {moment(data?.date).format("MMM DD, YYYY HH:mm")}
        </Text>
      </View>

      {/* Domain Information */}
      <View style={styles.section}>
        <Text style={styles.label}>Domain Information:</Text>
        <Text style={styles.text}> Domain: {data?.result?.domainInfo?.domain}</Text>
        <Text style={styles.text}> Registrar: {data?.result?.domainInfo?.registrar}</Text>
        <Text style={styles.text}> Expiry Date: {data?.result?.domainInfo?.expiry}</Text>
      </View>

      {/* Technology Stack */}
      <View style={styles.section}>
        <Text style={styles.label}>Technology Stack:</Text>
        <View style={styles.chipContainer}>
          {data?.result?.technologiesInfo?.technologiesUsed?.map((tech, index) => (
            <Text key={index} style={styles.chip}>
              {tech.name} ({tech.description})
            </Text>
          ))}
        </View>
      </View>

      {/* MX Records */}
      <View style={styles.section}>
        <Text style={styles.label}>Mail Exchange (MX) Records:</Text>
        {data?.result?.MXrecordsInfo?.MXRecord?.mxRecords?.length > 0 ? (
          data?.result?.MXrecordsInfo?.MXRecord?.mxRecords?.map((record, index) => (
            <Text key={index} style={styles.text}> {record}</Text>
          ))
        ) : <Text>No MX records found.</Text>}
      </View>

      {/* Host Reputation */}
      <View style={styles.section}>
        <Text style={styles.label}>Host Reputation:</Text>
        {data?.result?.hostReputation?.map((host, index) => (
          <Text key={index} style={styles.text}>
            {host.input} → {host.ip} {host.blacklisted ? " Blacklisted" : " Safe"}
          </Text>
        ))}
      </View>

      {/* Open Ports */}
      <View style={styles.section}>
        <Text style={styles.label}>Open Ports:</Text>
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <Text style={[styles.tableCell, styles.tableHeader]}>Host</Text>
            <Text style={[styles.tableCell, styles.tableHeader]}>Port</Text>
            <Text style={[styles.tableCell, styles.tableHeader]}>Service</Text>
          </View>
          {data?.result?.openportsInfo?.map((port, index) => (
            <View key={index} style={styles.tableRow}>
              <Text style={styles.tableCell}>{port.hostName}</Text>
              <Text style={styles.tableCell}>{port.port_id}</Text>
              <Text style={styles.tableCell}>{port.service_name}</Text>
            </View>
          ))}
        </View>
      </View>

      {/* Public URLs */}
      <View style={styles.section}>
        <Text style={styles.label}>Public URLs:</Text>
        {data?.result?.publicUrls?.map((url, index) => (
          <Text key={index} style={styles.text}>
            <Link src={url}>{url}</Link>
          </Text>
        ))}
      </View>

      {/* ✅ Sticky footer */}
      <Footer />
    </Page>
  </Document>
);

export default AttackSurfacePDF;
